import { ComponentPropsWithoutRef, useState } from 'react';

import classNames from 'classnames';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Modal, ModalCloseButton, ModalContent, useModalController } from '@uikit';

import AngleLeft from '@uikit/icons/AngleLeft';
import AngleRight from '@uikit/icons/AngleRight';

import { ReviewContentReviewData } from '@shared/api/types/review';

import ReviewCard from '../ReviewCard/ReviewCard';
import ReviewContent, { ReviewContentProps } from '../ReviewContent/ReviewContent';

type ReviewsSliderProps = ComponentPropsWithoutRef<'div'> &
  Pick<ReviewContentProps, 'reviewTitleTag' | 'app'> & {
    reviews: ReviewContentReviewData[];
    showReviewFlagInModal?: boolean;
    sliderClassName?: string;
    id: string;
  } & Pick<ReviewContentProps, 'showReviewFlag'>;

const ReviewsSlider = ({
  reviews,
  id,
  className,
  sliderClassName,
  reviewTitleTag,
  app,
  showReviewFlag,
  showReviewFlagInModal,
  ...props
}: ReviewsSliderProps) => {
  const [activeReview, setActiveReview] = useState<ReviewContentReviewData>();
  const moreModal = useModalController();
  const prevButtonId = `${id}-prev-button`;
  const nextButtonId = `${id}-next-button`;

  const openReviewMoreModal = (review: ReviewContentReviewData) => {
    setActiveReview(review);
    moreModal.open();
  };

  return (
    <>
      <div className={classNames(className)} {...props}>
        <div className="overflow-hidden flex items-center">
          <Swiper
            slidesPerView="auto"
            modules={[Navigation]}
            navigation={{
              prevEl: '#' + prevButtonId,
              nextEl: '#' + nextButtonId,
              lockClass: 'hidden',
              disabledClass: 'button-disabled',
            }}
            className={classNames('flex-1', sliderClassName)}
          >
            {reviews.map((review) => {
              return (
                <SwiperSlide
                  key={review.id}
                  className="w-[calc(100%_-_70px)] sm:w-72 mr-6 last:mr-0"
                  onClick={() => openReviewMoreModal(review)}
                  data-qa="review-slide"
                >
                  <ReviewCard
                    reviewTitleTag={reviewTitleTag}
                    review={review}
                    app={app}
                    showReviewFlag={showReviewFlag}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>

          <button
            id={prevButtonId}
            className="max-lg:hidden hidden lg:block icon-button button-ghost button-color-primary flex-shrink-0 mx-2.5"
            data-qa="review-previous-button"
          >
            <AngleLeft className="icon" />
          </button>
          <button
            id={nextButtonId}
            className="max-lg:hidden hidden lg:block icon-button button-ghost button-color-primary flex-shrink-0"
            data-qa="review-next-button"
          >
            <AngleRight className="icon" />
          </button>
        </div>
      </div>

      <Modal {...moreModal.props}>
        <ModalContent
          className="flex flex-col gap-8 p-10 pt-14 w-full max-w-xl"
          data-qa="review-modal"
        >
          <ModalCloseButton className="absolute right-4 top-4" />

          {activeReview && (
            <ReviewContent
              reviewTitleTag="h3"
              review={activeReview}
              app={app}
              showReviewFlag={showReviewFlagInModal}
              isFeedbackHorizontal
            />
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default ReviewsSlider;
